/**
 * PdfExportContext
 *
 * Carries the render scope of a PDF/email export subtree: the flag marking the
 * portal, plus the visibility view (SYD-1948) and split (SYD-2302) that subtree
 * renders at. Consumers are `useVisibilityMode` and `useArTableSplit`, which
 * prefer these overrides over the live `formStore` singletons so an export can
 * be built for a view or a split other than the one on screen without ever
 * mutating the on-screen form. Both overrides are undefined off-portal.
 */

import { createContext, useContext } from 'react';
import type { SplitQuery, VisibilityTypeId } from '#types/System.types';

export type PdfExportContextValue = {
  isPdfExportMode: boolean;
  /**
   * Visibility view this export subtree renders at (SYD-1948). Set only by the
   * portal in `PdfExportProvider`. When present, in-portal visibility readers
   * prefer it over the live `formStore.visibilityMode`, so export/email
   * rendering never mutates the on-screen view. Undefined off-portal, where
   * readers fall through to the live view.
   */
  visibilityMode?: VisibilityTypeId;
  /**
   * Split filter this export subtree renders at (SYD-2302). Set only by the
   * portal in `PdfExportProvider`. When present, `useArTableSplit` prefers it
   * over the live `formStore.splitQuery`, so a per-target document can be built
   * for one manufacturer while the on-screen form keeps its own split.
   * Undefined off-portal, where readers fall through to the live split.
   */
  splitQuery?: SplitQuery;
};

const defaultValue: PdfExportContextValue = {
  isPdfExportMode: false,
};

export const PdfExportContext = createContext<PdfExportContextValue>(defaultValue);

export const usePdfExportContext = () => useContext(PdfExportContext);

